import { Stack } from '@mui/material';
import { ChangeEvent, DragEvent, useCallback, useEffect, useRef, useState } from 'react';

import { TFetchError } from '@/api/api-error.types';
import UploadIcon from '@/assets/svg/ui-elements/upload-outline.svg';
import { Button, Spinner, theme, Typography } from '@/shared/design-system';
import { getApiErrorMessage } from '@/utils/api-error-message';

import { ocrBusinessCard } from '../../api';
import type { ContactData } from '../../types';
import { BusinessCardPreview, UploadContainer, UploadIconCircle } from './styled';

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const MAX_SIZE_BYTES = 10 * 1024 * 1024;

function formatFileSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface CardUploadStepProps {
  onExtractOk: (data: Partial<ContactData>) => void;
  onBack: () => void;
}

export const CardUploadStep = ({ onExtractOk, onBack }: CardUploadStepProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [isExtracting, setIsExtracting] = useState(false);

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  const validate = useCallback((file: File): string | null => {
    if (!ALLOWED_TYPES.includes(file.type))
      return 'Unsupported file type. Upload a JPG, PNG, WebP, or GIF image.';
    if (file.size > MAX_SIZE_BYTES) return 'File too large. Maximum size is 10 MB.';
    return null;
  }, []);

  const handleFile = useCallback(
    async (file: File) => {
      const validationError = validate(file);
      if (validationError) {
        setLocalError(validationError);
        return;
      }
      setLocalError(null);
      setPreview(URL.createObjectURL(file));
      setFileName(formatFileSize(file.size));
      setIsExtracting(true);
      try {
        const data = await ocrBusinessCard(file);
        onExtractOk(data);
      } catch (e) {
        setIsExtracting(false);
        setLocalError(getApiErrorMessage(e as TFetchError));
      }
    },
    [validate, onExtractOk],
  );

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFile(file);
      e.target.value = '';
    },
    [handleFile],
  );

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => setIsDragging(false), []);

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files?.[0];
      if (file) handleFile(file);
    },
    [handleFile],
  );

  const isIdle = !isExtracting && !preview;

  return (
    <Stack flexDirection="column" gap="24px">
      <input
        ref={inputRef}
        type="file"
        accept={ALLOWED_TYPES.join(',')}
        style={{ display: 'none' }}
        onChange={handleInputChange}
      />

      <UploadContainer
        $isIdle={isIdle}
        $isDragging={isDragging}
        onClick={isIdle ? () => inputRef.current?.click() : undefined}
        onDragOver={isIdle ? handleDragOver : undefined}
        onDragLeave={isIdle ? handleDragLeave : undefined}
        onDrop={isIdle ? handleDrop : undefined}
      >
        {isExtracting ? (
          <Stack
            sx={{
              width: 168,
              height: 168,
              borderRadius: '8px',
              background: theme.colors.white[4],
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Spinner />
          </Stack>
        ) : preview ? (
          <BusinessCardPreview src={preview} alt={fileName ?? undefined} />
        ) : (
          <Stack flexDirection="column" alignItems="center" gap="12px">
            <UploadIconCircle>
              <UploadIcon width={24} height={24} color="#99A1AF" />
            </UploadIconCircle>
            <Stack flexDirection="column" alignItems="center" gap="4px">
              <Typography variant="body3" align="center">
                Drag & drop an image here or click to upload
              </Typography>
              <Typography variant="body3" color={theme.colors.white[60]}>
                JPG, PNG, WebP, GIF • Max 10MB
              </Typography>
            </Stack>
          </Stack>
        )}
      </UploadContainer>

      {isExtracting && (
        <Stack flexDirection="row" alignItems="center" justifyContent="center" gap="10px">
          <Typography variant="body3" color={theme.colors.white[60]}>
            Extracting contact details…
          </Typography>
        </Stack>
      )}

      {localError && (
        <Typography variant="body3" color={theme.colors.text.error}>
          {localError}
        </Typography>
      )}

      {!isExtracting && (
        <Stack flexDirection="row" gap="8px">
          <Button color="tertiary" variant="outline" size="lg" onClick={onBack} fullWidth>
            Back
          </Button>
        </Stack>
      )}
    </Stack>
  );
};
